import { useState } from "react"
import { toast } from "sonner"
import { Plus, ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react"
import {
  useSubOrganizations,
  useCreateSubOrganization,
  useUpdateSubOrganization,
  useSubOrgAssignments,
  useUpdateSubOrgAssignments,
} from "@/lib/queries"
import type { SubOrganization } from "@/lib/types"
import { DOCUMENT_TYPE_LABELS } from "@/lib/documentTypes"
import { OrgUsersPanel } from "@/components/OrgUsersPanel"
import { SsoChangeConfirmDialog, ssoChanges, type SsoChange } from "@/components/SsoChangeConfirmDialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { Switch } from "@/components/ui/switch"
import { Checkbox } from "@/components/ui/checkbox"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog"
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table"

export function PharmacySitesPanel({ currentUserId }: { currentUserId: number }) {
  const { data, isLoading, isError } = useSubOrganizations()
  const [editing, setEditing] = useState<SubOrganization | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [selected, setSelected] = useState<SubOrganization | null>(null)

  if (isLoading) return <p className="text-muted-foreground py-6">Loading...</p>
  if (isError) return <p className="text-destructive py-6">Could not load pharmacy sites.</p>

  const subs = data?.sub_organizations ?? []
  const cap = data?.max_sub_organizations ?? 0
  const atCap = subs.length >= cap

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {subs.length} of {cap} pharmacy sites
        </p>
        <Button onClick={() => setCreateOpen(true)} disabled={atCap}>
          <Plus className="mr-2 h-4 w-4" />
          Add pharmacy site
        </Button>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Users</TableHead>
              <TableHead>SSO domain</TableHead>
              <TableHead className="w-32" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {subs.map((s) => (
              <TableRow key={s.id}>
                <TableCell className="font-medium">{s.name}</TableCell>
                <TableCell>
                  {s.active
                    ? <Badge variant="secondary">Active</Badge>
                    : <Badge variant="destructive">Deactivated</Badge>}
                </TableCell>
                <TableCell>{s.user_count}</TableCell>
                <TableCell>{s.sso_domain ?? "-"}</TableCell>
                <TableCell className="space-x-2 text-right">
                  <Button variant="ghost" size="sm" onClick={() => setEditing(s)}>
                    Edit
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setSelected(s)}>
                    Manage
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <PharmacySiteFormDialog
        open={createOpen || editing !== null}
        pharmacy={editing}
        onClose={() => { setCreateOpen(false); setEditing(null) }}
      />

      <Dialog open={selected !== null} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{selected?.name}</DialogTitle>
          </DialogHeader>
          {selected && (
            <Tabs defaultValue="users">
              <TabsList>
                <TabsTrigger value="users">Users</TabsTrigger>
                <TabsTrigger value="documents">Documents</TabsTrigger>
              </TabsList>
              <TabsContent value="users">
                <OrgUsersPanel currentUserId={currentUserId} subOrgId={selected.id} />
              </TabsContent>
              <TabsContent value="documents">
                <PharmacySiteDocumentsPanel subOrgId={selected.id} />
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

function PharmacySiteFormDialog({
  open,
  pharmacy,
  onClose,
}: {
  open: boolean
  pharmacy: SubOrganization | null
  onClose: () => void
}) {
  const createSub = useCreateSubOrganization()
  const updateSub = useUpdateSubOrganization()
  const [name, setName] = useState(pharmacy?.name ?? "")
  const [ssoDomain, setSsoDomain] = useState(pharmacy?.sso_domain ?? "")
  const [entraTenantId, setEntraTenantId] = useState(pharmacy?.entra_tenant_id ?? "")
  const [active, setActive] = useState(pharmacy?.active ?? true)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [pendingChanges, setPendingChanges] = useState<SsoChange[]>([])

  // Re-seed form when the dialog target changes
  const [seededFor, setSeededFor] = useState<number | null | undefined>(undefined)
  if (seededFor !== (pharmacy?.id ?? null)) {
    setSeededFor(pharmacy?.id ?? null)
    setName(pharmacy?.name ?? "")
    setSsoDomain(pharmacy?.sso_domain ?? "")
    setEntraTenantId(pharmacy?.entra_tenant_id ?? "")
    setActive(pharmacy?.active ?? true)
  }

  const doSubmit = async () => {
    try {
      if (pharmacy) {
        await updateSub.mutateAsync({
          id: pharmacy.id,
          name,
          sso_domain: ssoDomain || null,
          entra_tenant_id: entraTenantId || null,
          active,
        })
        toast.success("Pharmacy site updated")
      } else {
        await createSub.mutateAsync({
          name,
          sso_domain: ssoDomain || undefined,
          entra_tenant_id: entraTenantId || undefined,
        })
        setName("")
        setSsoDomain("")
        setEntraTenantId("")
        setActive(true)
        toast.success("Pharmacy site created")
      }
      onClose()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not save pharmacy site")
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (pharmacy) {
      const changes = ssoChanges(
        {
          sso_domain: pharmacy.sso_domain,
          entra_tenant_id: pharmacy.entra_tenant_id,
        },
        {
          sso_domain: ssoDomain.trim() || null,
          entra_tenant_id: entraTenantId.trim() || null,
        }
      )
      if (changes.length > 0) {
        setPendingChanges(changes)
        setConfirmOpen(true)
        return
      }
    }
    void doSubmit()
  }

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{pharmacy ? "Edit pharmacy site" : "Add pharmacy site"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="pharmacy-name">Name</Label>
              <Input
                id="pharmacy-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pharmacy-sso">Google SSO domain</Label>
              <Input
                id="pharmacy-sso"
                value={ssoDomain}
                onChange={(e) => setSsoDomain(e.target.value)}
                placeholder="pharmacy.example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pharmacy-entra">Microsoft Entra tenant ID</Label>
              <Input
                id="pharmacy-entra"
                value={entraTenantId}
                onChange={(e) => setEntraTenantId(e.target.value)}
              />
            </div>
            {pharmacy && (
              <div className="flex items-center gap-2">
                <Switch id="pharmacy-active" checked={active} onCheckedChange={setActive} />
                <Label htmlFor="pharmacy-active">
                  {active ? "Active" : "Deactivated (blocks all logins)"}
                </Label>
              </div>
            )}
            <DialogFooter>
              <Button type="submit" disabled={createSub.isPending || updateSub.isPending}>
                {pharmacy ? "Save" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <SsoChangeConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        changes={pendingChanges}
        onConfirm={() => {
          setConfirmOpen(false)
          void doSubmit()
        }}
      />
    </>
  )
}

type SortKey = "title" | "document_type"

function PharmacySiteDocumentsPanel({ subOrgId }: { subOrgId: number }) {
  const { data: assignments, isLoading, isError } = useSubOrgAssignments(subOrgId)
  const updateAssignments = useUpdateSubOrgAssignments(subOrgId)
  const [changes, setChanges] = useState<Record<number, boolean>>({})
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 } | null>(null)

  if (isLoading) return <p className="text-muted-foreground py-6">Loading...</p>
  if (isError) return <p className="text-destructive py-6">Could not load documents.</p>

  const rows = assignments ?? []
  const isDirty = Object.keys(changes).length > 0

  const typeLabel = (t: string) => DOCUMENT_TYPE_LABELS[t] || t
  // ponytail: client-side sort; move server-side if this list gets paginated
  const sortedRows = sort
    ? [...rows].sort((a, b) => {
        const av = sort.key === "title" ? a.title : typeLabel(a.document_type)
        const bv = sort.key === "title" ? b.title : typeLabel(b.document_type)
        return av.toLowerCase().localeCompare(bv.toLowerCase()) * sort.dir
      })
    : rows

  const toggleSort = (key: SortKey) =>
    setSort((s) => ({ key, dir: s?.key === key && s.dir === 1 ? -1 : 1 }))

  const effective = (a: { document_id: number; active: boolean }) =>
    changes[a.document_id] ?? a.active
  const onCount = rows.filter(effective).length
  const allState: boolean | "indeterminate" =
    onCount === 0 ? false : onCount === rows.length ? true : "indeterminate"

  const setAll = (target: boolean) => {
    const next: Record<number, boolean> = {}
    for (const a of rows) {
      if (a.active !== target) next[a.document_id] = target
    }
    setChanges(next)
  }

  const handleSave = async () => {
    try {
      await updateAssignments.mutateAsync(
        Object.entries(changes).map(([documentId, active]) => ({
          document_id: Number(documentId),
          active,
        }))
      )
      setChanges({})
      toast.success("Documents updated")
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not update documents")
    }
  }

  const SortIcon = ({ sortKey }: { sortKey: SortKey }) =>
    sort?.key === sortKey ? (
      sort.dir === 1 ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
    ) : (
      <ChevronsUpDown className="h-3 w-3 opacity-40" />
    )

  return (
    <div className="space-y-4">
      <div className="rounded-md border max-h-80 overflow-y-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox
                  checked={allState}
                  onCheckedChange={() => setAll(allState !== true)}
                  aria-label="Select all documents"
                />
              </TableHead>
              <TableHead>
                <button
                  type="button"
                  className="flex items-center gap-1 hover:text-foreground"
                  onClick={() => toggleSort("title")}
                >
                  Document
                  <SortIcon sortKey="title" />
                </button>
              </TableHead>
              <TableHead>
                <button
                  type="button"
                  className="flex items-center gap-1 hover:text-foreground"
                  onClick={() => toggleSort("document_type")}
                >
                  Type
                  <SortIcon sortKey="document_type" />
                </button>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedRows.map((a) => (
              <TableRow key={a.document_id}>
                <TableCell>
                  <Checkbox
                    checked={changes[a.document_id] ?? a.active}
                    onCheckedChange={(checked) =>
                      setChanges((c) => {
                        const next = { ...c }
                        if (checked === true === a.active) {
                          delete next[a.document_id]
                        } else {
                          next[a.document_id] = checked === true
                        }
                        return next
                      })
                    }
                  />
                </TableCell>
                <TableCell>{a.title}</TableCell>
                <TableCell className="text-muted-foreground">{typeLabel(a.document_type)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <Button onClick={handleSave} disabled={!isDirty || updateAssignments.isPending}>
        {updateAssignments.isPending ? "Saving..." : "Save documents"}
      </Button>
    </div>
  )
}
